// AB#1753 — C7 calendar realtime subscriber hook (FE half of Calendar v1).
//
// Subscribes to the `appointment.changed` event on `/hubs/calendar` and:
//   - Invalidates `queryKeys.calendar.all` so any open calendar grid /
//     availability picker refetches immediately.
//   - Pushes a top-right toast: "{actorDisplayName} just booked
//     {serviceName} at {startTime}".
//
// Mount via `<CalendarRealtimeBridge />` once per authenticated owner
// session — the bridge component lives high in App.tsx and the hook
// guards against double-mount via a module-level singleton ref.

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as signalR from '@microsoft/signalr';
import { buildCalendarConnection } from '../services/calendarRealtime';
import { queryKeys } from '../lib/queryKeys';
import { useAuthContext } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import type { AppointmentChangedNotification } from '../types/api-responses';

// Module-level singleton. Two mounts of <CalendarRealtimeBridge /> share
// the same connection — useful in dev with React 18 strict-mode double-
// invocation, and a defence against the bridge accidentally landing on
// two pages.
let activeConnection: signalR.HubConnection | null = null;
let activeConnectionTokenSig: string | null = null;

/**
 * Compute a lightweight signature for the current token so we can detect
 * a profile switch (new token, different `businessId` claim) and rebuild
 * the connection. We avoid decoding the JWT — the leading 64 chars are
 * sufficient as a delta detector.
 */
function tokenSignature(): string {
    try {
        const t = window.localStorage.getItem('accessToken');
        return t ? t.slice(0, 64) : '';
    } catch {
        return '';
    }
}

export function useCalendarRealtime(): void {
    const qc = useQueryClient();
    const { isAuthenticated, accessToken } = useAuthContext();
    const { showToast } = useToast();

    useEffect(() => {
        if (!isAuthenticated || !accessToken) return;

        const sig = tokenSignature();

        // Reuse existing connection if the token signature matches. On
        // profile switch the sig changes — we tear down + rebuild below.
        if (activeConnection && activeConnectionTokenSig === sig) {
            return;
        }

        // Stop any prior connection (e.g. from a stale profile).
        if (activeConnection) {
            const prev = activeConnection;
            activeConnection = null;
            activeConnectionTokenSig = null;
            prev.stop().catch(() => { /* swallow */ });
        }

        const connection = buildCalendarConnection();

        connection.on('appointment.changed', (notification: AppointmentChangedNotification) => {
            // Cache invalidation — refetches any open calendar grid /
            // availability picker for the affected business.
            qc.invalidateQueries({ queryKey: queryKeys.calendar.all });

            // Toast — single-line summary. Verb varies by action.
            const verb = notification.action === 'created' ? 'just booked'
                : notification.action === 'updated' ? 'just updated'
                : 'just removed';
            const subject = notification.actorDisplayName?.trim() || 'Someone';
            const message = `${subject} ${verb} ${notification.serviceName} at ${notification.startTime}`;
            showToast(message, 'info');
        });

        // After an automatic reconnect, the server has already re-joined
        // us to our `business:{id}` group. Refresh the cache so we catch
        // any events we missed while disconnected.
        connection.onreconnected(() => {
            qc.invalidateQueries({ queryKey: queryKeys.calendar.all });
        });

        let cancelled = false;
        connection.start()
            .then(() => {
                if (!cancelled) {
                    activeConnection = connection;
                    activeConnectionTokenSig = sig;
                }
            })
            .catch((err: Error) => {
                if (!cancelled) {
                    console.error('CalendarHub connection error:', err);
                }
            });

        return () => {
            cancelled = true;
            // Only stop if WE own the connection — bridge unmount on
            // signout will hit this; profile-switch tears the old conn
            // down in the next mount.
            if (activeConnection === connection) {
                activeConnection = null;
                activeConnectionTokenSig = null;
                connection.stop().catch(() => { /* swallow */ });
            }
        };
    }, [isAuthenticated, accessToken, qc, showToast]);

    // Profile-switch event — AuthContext dispatches `devanza:profile-switched`
    // on a successful switch-profile call. Re-evaluate the token sig and
    // tear down + rebuild on the next useEffect tick.
    useEffect(() => {
        const handler = () => {
            if (activeConnection) {
                const prev = activeConnection;
                activeConnection = null;
                activeConnectionTokenSig = null;
                prev.stop().catch(() => { /* swallow */ });
            }
            // Invalidate calendar caches so the new profile's data
            // appears on next render without stale grid rows.
            qc.invalidateQueries({ queryKey: queryKeys.calendar.all });
        };
        window.addEventListener('devanza:profile-switched', handler);
        return () => window.removeEventListener('devanza:profile-switched', handler);
    }, [qc]);
}
